/**
 * Menu mobile Wouska : navigation entre panneaux (menu principal / animal)
 * et accordéon des catégories. Aucune dépendance externe.
 * L'ouverture, la fermeture (Échap inclus) et le blocage du scroll sont gérés
 * par le tiroir du thème (header-drawer).
 */
class WouskaMobileMenu extends HTMLElement {
  #lastOpener = null;

  connectedCallback() {
    this.details = this.closest('details');
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.details?.addEventListener('toggle', this.#onDrawerToggle);
    this.#showPanel('wmm-root', { animate: false, focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.details?.removeEventListener('toggle', this.#onDrawerToggle);
  }

  get #panels() {
    return Array.from(this.querySelectorAll('[data-wmm-panel]'));
  }

  #onClick = (event) => {
    const opener = event.target.closest('[data-wmm-open]');
    if (opener && this.contains(opener)) {
      this.#lastOpener = opener;
      this.#showPanel(opener.dataset.wmmOpen, { direction: 'forward' });
      return;
    }

    if (event.target.closest('[data-wmm-back]')) {
      this.#showPanel('wmm-root', { direction: 'back' });
      return;
    }

    const toggle = event.target.closest('[data-wmm-toggle]');
    if (toggle && this.contains(toggle)) {
      this.#toggleCategory(toggle);
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'Tab') {
      this.#trapFocus(event);
      return;
    }

    const head = event.target.closest('.wmm-cat__head');
    if (!head || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

    const panel = head.closest('[data-wmm-panel]');
    const heads = Array.from(panel.querySelectorAll('.wmm-cat__head'));
    const index = heads.indexOf(head);
    let next = index;
    if (event.key === 'ArrowDown') next = Math.min(index + 1, heads.length - 1);
    if (event.key === 'ArrowUp') next = Math.max(index - 1, 0);
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = heads.length - 1;
    event.preventDefault();
    heads[next]?.focus();
  };

  #onDrawerToggle = () => {
    if (this.details.open) return;
    this.#collapseAll();
    this.#showPanel('wmm-root', { animate: false, focus: false });
  };

  #showPanel(id, { direction = 'forward', animate = true, focus = true } = {}) {
    const target = this.querySelector(`#${CSS.escape(id)}`);
    if (!target) return;

    for (const panel of this.#panels) {
      const isTarget = panel === target;
      panel.hidden = !isTarget;
      panel.classList.remove('is-entering-forward', 'is-entering-back');
    }

    target.scrollTop = 0;

    if (animate && !this.reduceMotion.matches) {
      target.classList.add(direction === 'back' ? 'is-entering-back' : 'is-entering-forward');
    }

    if (!focus) return;

    if (id === 'wmm-root') {
      (this.#lastOpener ?? target.querySelector('a, button'))?.focus({ preventScroll: true });
    } else {
      target.querySelector('.wmm-title')?.focus({ preventScroll: true });
    }
  }

  #toggleCategory(button) {
    const open = button.getAttribute('aria-expanded') !== 'true';
    const list = button.closest('.wmm-cats');

    if (open && list) {
      for (const other of list.querySelectorAll('[data-wmm-toggle][aria-expanded="true"]')) {
        if (other !== button) this.#setCategory(other, false);
      }
    }

    this.#setCategory(button, open);

    if (open && !this.reduceMotion.matches) {
      const card = button.closest('.wmm-cat__card');
      window.setTimeout(() => card?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 340);
    }
  }

  #setCategory(button, open) {
    const card = button.closest('.wmm-cat__card');
    const body = document.getElementById(button.getAttribute('aria-controls'));
    button.setAttribute('aria-expanded', String(open));
    card?.classList.toggle('is-open', open);
    if (body) body.inert = !open;
  }

  #collapseAll() {
    for (const button of this.querySelectorAll('[data-wmm-toggle][aria-expanded="true"]')) {
      this.#setCategory(button, false);
    }
  }

  #trapFocus(event) {
    const selector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(this.querySelectorAll(selector)).filter(
      (el) => !el.closest('[inert], [hidden]') && el.getClientRects().length > 0
    );
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !this.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('wouska-mobile-menu')) {
  customElements.define('wouska-mobile-menu', WouskaMobileMenu);
}
